import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Share2 } from "lucide-react";
import { FavouriteButton } from "@/components/FavouriteButton";
import { ShareCardModal } from "@/components/ShareCardModal";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const formatYear = (y) => (y == null ? "—" : y < 0 ? `${-y} BC` : String(y));
const dateLabel = (ddmm) => {
  const [d, m] = String(ddmm || "").split("-");
  return d && m ? `${Number(d)} ${MONTHS[Number(m) - 1] || ""}` : "";
};

const ART_ALIAS = { "global-event": "global", "song-release": "songs", "film-release": "tv" };
export const categorySlug = (category) => {
  const s = String(category || "event").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return ART_ALIAS[s] || s;
};

const BADGE = "bg-paper text-ink border-gold";

export const eventDescription = (e) =>
  e.description_long || e.Description_OddlyTrue_Long || e.description || e.Description_Original_CopyrightChecked || `${e.category} milestone: ${e.event_title} in ${formatYear(e.year)}.`;

export const EventCard = ({ e, i, total = 0, showDate = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const desc = eventDescription(e);
  const hook = e.hook || (e.links_notes && !/^Links: other events/i.test(e.links_notes) ? e.links_notes : null)
    || `Happened on ${e.date_dd_mm} — same date as ${Math.max(total - 1, 0)} other historic moments.`;
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(i * 0.04, 0.4) }}
      data-testid={`event-row-${e.id}`}
      className="flex gap-4 p-4 rounded-xl bg-white border border-[#e5e7eb] hover:shadow-md hover:shadow-black/5 transition-shadow"
    >
      <img
        src={e.portrait || `/images/categories/${categorySlug(e.category)}.png`}
        onError={(ev) => { ev.currentTarget.onerror = null; ev.currentTarget.src = "/images/categories/event.png"; }}
        alt={e.portrait ? e.event_title : e.category}
        width={72}
        height={72}
        loading="lazy"
        data-testid="event-thumb"
        className={`w-[72px] h-[72px] shrink-0 rounded-lg object-cover bg-paper ${e.portrait ? "object-top" : ""}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-bold text-lg text-gold leading-none">{formatYear(e.year)}</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.08em] border ${BADGE}`}>{e.category}</span>
          {e.old_style && <span data-testid="event-old-style" title={e.calendar_note || "Old Style (Julian calendar) date, as recorded at the time. Britain adopted the Gregorian calendar in 1752."} className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.08em] border border-ink/30 bg-paper text-ink-muted cursor-help">O.S. date</span>}
          {e.corrected_at && <span data-testid="event-corrected" title={`Corrected ${e.corrected_at.slice(0, 10)} after a reader report${e.corrected_note ? `: ${e.corrected_note}` : ""}`} className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.08em] border border-emerald-700/40 bg-emerald-50 text-emerald-800 cursor-help">Corrected</span>}
          {showDate && <Link to={`/?date=${e.date_dd_mm}&year=${e.year}`} data-testid="event-date-link" className="text-[11px] font-semibold text-gold hover:underline">{e.date_dd_mm}</Link>}
          <FavouriteButton factId={e.id} variant="inline" date={e.date_dd_mm} category={e.category} />
          <button
            type="button"
            data-testid={`event-share-${e.id}`}
            onClick={() => setShareOpen(true)}
            aria-label="Share this event"
            title="Share this event"
            className="w-7 h-7 rounded-full border border-[#e5e7eb] bg-white text-ink-muted hover:border-gold hover:text-gold flex items-center justify-center transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
          {e.is_placeholder && (
            <span data-testid="verified-badge" className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.08em] border border-gold bg-white text-gold">
              Verified • Original Summary
            </span>
          )}
        </div>
        <h4 className="text-lg font-semibold text-ink leading-snug">{e.event_title}</h4>
        <p data-testid="event-desc" className={`mt-1 text-[14px] leading-[1.65] text-[#333] ${expanded ? "" : "line-clamp-3"}`}>{desc}</p>
        <button
          data-testid="event-read-more"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className="text-gold text-[12px] font-semibold mt-2 hover:underline"
        >
          {expanded ? "Show less ↑" : "Read more →"}
        </button>
        {expanded && (
          <div data-testid="event-hook" className="mt-3 p-4 bg-paper border-l-4 border-gold rounded-r-xl text-[13px] leading-[1.7] text-ink">
            <p className="font-semibold">Why it's OddlyTrue:</p>
            <p>{hook}</p>
            {e.sources && <p className="mt-2 text-[11px] text-ink-muted">Source: {e.sources}</p>}
          </div>
        )}
      </div>
      <ShareCardModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        heading="Share this event"
        analyticsName="event_card"
        testPrefix="event-card"
        shareUrl={`${window.location.origin}/?date=${e.date_dd_mm}&year=${e.year}`}
        card={{ badge: e.category, greeting: `On this day in ${e.year == null ? "history" : formatYear(e.year)}`, title: e.event_title, body: desc, dateLabel: dateLabel(e.date_dd_mm) }}
      />
    </motion.article>
  );
};
